import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { defaultSnoozeMs, useNoticeboard } from './useNoticeboard.js';
import NoticeboardModal from './NoticeboardModal.js';

const NoticeboardContext = createContext(null);

export const NoticeboardProvider = ({ children, autoPresent = false }) => {
  const {
    visibleNotices,
    standardNotices,
    persistentNotices,
    unseenNotices,
    isDue,
    isLoginDue,
    isSnoozeWake,
    isComplete,
    getData,
    snooze,
    dismiss,
    saveData,
    nextWakeAt,
    isLoading
  } = useNoticeboard();
  const [isBoardOpen, setIsBoardOpen] = useState(false);
  const [boardNotice, setBoardNotice] = useState(null);
  const [persistentNotice, setPersistentNotice] = useState(null);
  const isBoardOpenRef = useRef(false);
  const persistentNoticeRef = useRef(null);
  const didInitialPresent = useRef(false);
  isBoardOpenRef.current = isBoardOpen;
  persistentNoticeRef.current = persistentNotice;

  const closeBoard = useCallback(() => {
    setIsBoardOpen(false);
    setBoardNotice(null);
  }, []);

  const openNoticeboard = useCallback(() => {
    setBoardNotice(standardNotices[0] || null);
    setIsBoardOpen(true);
  }, [standardNotices]);

  const presentStandardOnLogin = useCallback(() => {
    if (!autoPresent || isLoading || isBoardOpenRef.current) return;
    const startNotice = standardNotices.find(isLoginDue);
    if (startNotice) {
      setBoardNotice(startNotice);
      setIsBoardOpen(true);
    }
  }, [autoPresent, isLoading, standardNotices, isLoginDue]);

  const presentPersistent = useCallback((predicate) => {
    if (!autoPresent || isLoading || persistentNoticeRef.current) return;
    const startNotice = persistentNotices.find(predicate);
    if (startNotice) {
      setPersistentNotice(startNotice);
    }
  }, [autoPresent, isLoading, persistentNotices]);

  useEffect(() => {
    if (!autoPresent || isLoading || didInitialPresent.current) return;
    didInitialPresent.current = true;
    presentStandardOnLogin();
    presentPersistent(isLoginDue);
  }, [autoPresent, isLoading, presentStandardOnLogin, presentPersistent, isLoginDue]);

  // If the open notice leaves the visible set, move on without recording a view.
  useEffect(() => {
    if (!boardNotice) return;
    if (standardNotices.some(notice => notice.noticeId === boardNotice.noticeId)) return;
    const nextNotice = standardNotices[0] || null;
    if (nextNotice) {
      setBoardNotice(nextNotice);
      return;
    }
    closeBoard();
  }, [boardNotice, standardNotices, closeBoard]);

  useEffect(() => {
    if (!persistentNotice) return;
    if (persistentNotices.some(notice => notice.noticeId === persistentNotice.noticeId)) return;
    setPersistentNotice(null);
  }, [persistentNotice, persistentNotices]);

  useEffect(() => {
    if (!autoPresent || isLoading || nextWakeAt == null) return;
    const delay = Math.max(0, nextWakeAt - Date.now()) + 50;
    const timer = setTimeout(() => presentPersistent(isSnoozeWake), delay);
    return () => clearTimeout(timer);
  }, [autoPresent, isLoading, nextWakeAt, presentPersistent, isSnoozeWake]);

  useEffect(() => {
    if (!autoPresent) return;
    const onWake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      presentPersistent(isSnoozeWake);
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [autoPresent, presentPersistent, isSnoozeWake]);

  const leaveNotice = useCallback((notice) => {
    if (!notice || isComplete(notice)) return;
    const durationMs = defaultSnoozeMs(notice);
    if (durationMs != null) {
      snooze(notice.noticeId, durationMs);
    } else {
      dismiss(notice.noticeId);
    }
  }, [dismiss, isComplete, snooze]);

  const handleBoardClose = useCallback(() => {
    leaveNotice(boardNotice);
    closeBoard();
  }, [boardNotice, closeBoard, leaveNotice]);

  const handleBoardPageChange = useCallback((index) => {
    const nextNotice = standardNotices[index];
    if (!nextNotice || nextNotice.noticeId === boardNotice?.noticeId) return;
    leaveNotice(boardNotice);
    setBoardNotice(nextNotice);
  }, [boardNotice, leaveNotice, standardNotices]);

  const goToNextStandardOrClose = useCallback((currentId) => {
    const currentIndex = standardNotices.findIndex(notice => notice.noticeId === currentId);
    const nextNotice = currentIndex >= 0 ? standardNotices[currentIndex + 1] : null;
    if (nextNotice) {
      setBoardNotice(nextNotice);
      return;
    }
    closeBoard();
  }, [closeBoard, standardNotices]);

  const handleGotIt = useCallback(() => {
    if (!boardNotice) return;
    const currentId = boardNotice.noticeId;
    leaveNotice(boardNotice);
    goToNextStandardOrClose(currentId);
  }, [boardNotice, goToNextStandardOrClose, leaveNotice]);

  const handleBoardSave = useCallback((data) => {
    if (!boardNotice) return;
    saveData(boardNotice.noticeId, data);
  }, [boardNotice, saveData]);

  const handleBoardComplete = useCallback((data) => {
    if (!boardNotice) return;
    const currentId = boardNotice.noticeId;
    if (data !== undefined) {
      saveData(currentId, data);
    } else {
      leaveNotice(boardNotice);
    }
    goToNextStandardOrClose(currentId);
  }, [boardNotice, goToNextStandardOrClose, leaveNotice, saveData]);

  const advancePersistent = useCallback((currentId) => {
    const nextNotice = persistentNotices.find(notice => (
      notice.noticeId !== currentId && (isLoginDue(notice) || isSnoozeWake(notice))
    ));
    setPersistentNotice(nextNotice || null);
  }, [isLoginDue, isSnoozeWake, persistentNotices]);

  const handlePersistentSave = useCallback((data) => {
    if (!persistentNotice) return;
    saveData(persistentNotice.noticeId, data);
  }, [persistentNotice, saveData]);

  const handlePersistentComplete = useCallback((data) => {
    if (!persistentNotice) return;
    const currentId = persistentNotice.noticeId;
    if (data !== undefined) {
      saveData(currentId, data);
    } else {
      leaveNotice(persistentNotice);
    }
    advancePersistent(currentId);
  }, [advancePersistent, leaveNotice, persistentNotice, saveData]);

  const handlePersistentSnooze = useCallback((durationMs) => {
    if (!persistentNotice) return;
    const currentId = persistentNotice.noticeId;
    snooze(currentId, durationMs);
    advancePersistent(currentId);
  }, [advancePersistent, persistentNotice, snooze]);

  const value = useMemo(() => ({
    openNoticeboard,
    visibleNotices,
    unseenNotices,
    isLoading
  }), [openNoticeboard, visibleNotices, unseenNotices, isLoading]);

  return (
    <NoticeboardContext.Provider value={value}>
      {children}
      <NoticeboardModal
        isOpen={isBoardOpen}
        visibleNotices={standardNotices}
        isDue={isDue}
        isComplete={isComplete}
        getData={getData}
        selectedNotice={boardNotice}
        onGotIt={handleGotIt}
        onSave={handleBoardSave}
        onComplete={handleBoardComplete}
        onPageChange={handleBoardPageChange}
        onClose={handleBoardClose}
      />
      <NoticeboardModal
        isOpen={Boolean(persistentNotice)}
        stacked
        visibleNotices={persistentNotice ? [persistentNotice] : []}
        isDue={isDue}
        isComplete={isComplete}
        getData={getData}
        selectedNotice={persistentNotice}
        onSave={handlePersistentSave}
        onComplete={handlePersistentComplete}
        onSnooze={handlePersistentSnooze}
      />
    </NoticeboardContext.Provider>
  );
};

export const useNoticeboardContext = () => {
  const context = useContext(NoticeboardContext);
  if (!context) {
    throw new Error('useNoticeboardContext must be used within a NoticeboardProvider');
  }
  return context;
};
